
import Vue from 'vue';
import { mapGetters } from 'vuex';
import Logging from '@/logging';
import { parseTime } from '@/utils';
import gql from 'graphql-tag';
import RsltAgGrid from '@/components/RsltAgGrid/index.vue';
import { attachmentDownload } from '@/api/attachment';
import { Uri_Attachment } from '@/schema/attachment';

const GET_REPORT_EXECUTION_LIST = gql`
	query ViewReports_reportExecutionList(
		$participant: uuid!
		$person: uuid!
		$legalEntity: uuid
		$dateFrom: timestamptz
		$dateTo: timestamptz
	) {
		reportExecutions: risolto_fe_report_execution_list(
			args: {
				p_participant: $participant
				p_person: $person
				p_legal_entity: $legalEntity
				p_date_from: $dateFrom
				p_date_to: $dateTo
			}
		) {
			data
			participant
		}
	}
`;

const GET_REPORT_DEFINITION_LIST = gql`
	query ViewReports_reportDefinitionList(
		$participant: uuid!
		$legalEntity: uuid
		$dateFrom: timestamptz
		$dateTo: timestamptz
	) {
		reportDefinitions: risolto_fe_report_definition_list(
			args: {
				p_participant: $participant
				p_legal_entity: $legalEntity
				p_date_from: $dateFrom
				p_date_to: $dateTo
			}
		) {
			data
			participant
		}
	}
`;

export default Vue.extend({
	name: 'ReportsIndex',
	components: {
		RsltAgGrid
	},
	data() {
		return {
			activeTab: 'executions',
			loading: false,
			dateFilter: 'past3Months',
			customDateRange: null,
			appliedFilters: {
				dateFilter: 'past3Months',
				customDateRange: null
			},
			reportDefinitions: [] as any[],
			reportExecutions: [] as any[],
			page: 1,
			limit: 25,
			offset: 0,
			gridApi: null as any,
			columnDefs: [] as any[]
		};
	},
	computed: {
		...mapGetters(['selectedParticipantStore', 'selectedLegalEntityStore', 'loggedInUser']),
		reports() {
			return this.activeTab === 'definitions' ? this.reportDefinitions : this.reportExecutions;
		},
		total() {
			return this.reports.length;
		},
		currentFilters() {
			return {
				dateFilter: this.dateFilter,
				customDateRange: this.customDateRange
			};
		},
		defaultFilters() {
			return {
				dateFilter: 'past3Months',
				customDateRange: null
			};
		},
		filtersChanged() {
			return JSON.stringify(this.currentFilters) !== JSON.stringify(this.appliedFilters);
		},
		canReset() {
			return JSON.stringify(this.currentFilters) !== JSON.stringify(this.defaultFilters);
		}
	},
	watch: {
		activeTab() {
			// Just update column defs when switching tabs
			this.initializeColumnDefs();
		}
	},
	created() {
		this.initializeColumnDefs();
		this.fetch();
	},
	methods: {
		initializeColumnDefs() {
			if (this.activeTab === 'definitions') {
				this.initializeDefinitionColumnDefs();
			} else {
				this.initializeExecutionColumnDefs();
			}
		},
		initializeDefinitionColumnDefs() {
			this.columnDefs = [
				{
					headerName: this.$t('reports.report_name'),
					field: 'name',
					width: 400,
					sortable: true,
					filter: 'agTextColumnFilter'
				},
				{
					headerName: this.$t('reports.frequency_expression'),
					field: 'frequency_expression',
					width: 180,
					sortable: true,
					filter: 'agTextColumnFilter',
					valueFormatter: (params: any) => params.value || '-'
				},
				{
					headerName: this.$t('reports.last_execution'),
					field: 'last_execution_date',
					width: 150,
					sortable: true,
					filter: 'agDateColumnFilter',
					valueFormatter: (params: any) => this.formatDate(params.value)
				}
			];
		},
		initializeExecutionColumnDefs() {
			this.columnDefs = [
				{
					headerName: this.$t('reports.completed_at'),
					field: 'execution_date',
					width: 130,
					sortable: true,
					sort: 'desc',
					filter: 'agDateColumnFilter',
					valueFormatter: (params: any) => this.formatDate(params.value)
				},
				{
					headerName: this.$t('reports.report_name'),
					field: 'name',
					width: 400,
					sortable: true,
					filter: 'agTextColumnFilter'
				},
				{
					headerName: this.$t('reports.users'),
					field: 'users',
					width: 250,
					sortable: true,
					filter: 'agTextColumnFilter',
					valueFormatter: (params: any) => params.value || '-'
				},
				{
					headerName: this.$t('reports.actions'),
					field: 'actions',
					width: 120,
					pinned: 'right',
					cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'flex-start' },
					cellRenderer: (params: any) => {
						const container = document.createElement('div');
						container.style.display = 'flex';
						container.style.gap = '8px';
						container.style.alignItems = 'center';

						const downloadBtn = document.createElement('button');
						downloadBtn.className = 'el-button el-button--primary el-button--mini';
						downloadBtn.textContent = this.$t('reports.download');

						if (!params.data.report_path) {
							downloadBtn.disabled = true;
							downloadBtn.className += ' is-disabled';
							downloadBtn.title = this.$t('reports.report_expired');
						}

						downloadBtn.addEventListener('click', (e) => {
							e.stopPropagation();
							if (params.data.report_path) {
								this.handleDownload(params.data);
							}
						});
						container.appendChild(downloadBtn);

						return container;
					}
				}
			];
		},
		async fetch() {
			this.loading = true;
			try {
				await Promise.all([
					// this.fetchDefinitions(),
					this.fetchExecutions()
				]);
			} finally {
				this.loading = false;
			}
		},
		async fetchDefinitions() {
			try {
				const variables = {
					participant: this.selectedParticipantStore,
					legalEntity: this.selectedLegalEntityStore
				};

				const result = await this.$apollo.query({
					query: GET_REPORT_DEFINITION_LIST,
					variables
				});

				if (result.data && result.data.reportDefinitions) {
					this.reportDefinitions = result.data.reportDefinitions.map((item: any) => ({ ...item.data }));
				}
			} catch (e) {
				Logging.error('Error fetching report definitions:', e);
				this.$message.error(this.$t('error.fetchFailed'));
			}
		},
		async fetchExecutions() {
			try {
				const variables: any = {
					participant: this.selectedParticipantStore,
					person: this.loggedInUser.reference,
					legalEntity: this.selectedLegalEntityStore
				};

				const dateRange = this.getDateFilterRange();
				if (dateRange) {
					variables.dateFrom = dateRange.start;
					variables.dateTo = dateRange.end;
				}

				const result = await this.$apollo.query({
					query: GET_REPORT_EXECUTION_LIST,
					variables
				});

				if (result.data && result.data.reportExecutions) {
					this.reportExecutions = result.data.reportExecutions.map((item: any) => ({ ...item.data }));
				}

				this.$nextTick(() => {
					if (this.gridApi) {
						this.gridApi.autoSizeAllColumns();
					}
				});
			} catch (e) {
				Logging.error('Error fetching report executions:', e);
				this.$message.error(this.$t('error.fetchFailed'));
			}
		},
		getDateFilterRange() {
			if (this.dateFilter === 'all') {
				return null;
			}

			const now = new Date();
			let start: Date;
			let end = new Date(now);

			if (this.dateFilter === 'custom' && this.customDateRange && this.customDateRange.length === 2) {
				start = new Date(this.customDateRange[0]);
				end = new Date(this.customDateRange[1]);
			} else if (this.dateFilter === 'past3Months') {
				start = new Date(now);
				start.setMonth(start.getMonth() - 3);
			} else if (this.dateFilter === 'past6Months') {
				start = new Date(now);
				start.setMonth(start.getMonth() - 6);
			} else if (this.dateFilter === 'past12Months') {
				start = new Date(now);
				start.setMonth(start.getMonth() - 12);
			} else if (this.dateFilter === '2025') {
				start = new Date(2025, 0, 1);
				end = new Date(2025, 11, 31, 23, 59, 59);
			} else if (this.dateFilter === '2024') {
				start = new Date(2024, 0, 1);
				end = new Date(2024, 11, 31, 23, 59, 59);
			} else if (this.dateFilter === '2023') {
				start = new Date(2023, 0, 1);
				end = new Date(2023, 11, 31, 23, 59, 59);
			} else {
				return null;
			}

			return { start: start.toISOString(), end: end.toISOString() };
		},
		handleApplyFilters() {
			this.appliedFilters = {
				dateFilter: this.dateFilter,
				customDateRange: this.customDateRange ? [...this.customDateRange] : null
			};

			this.fetchExecutions();
			if (this.gridApi) {
				this.gridApi.paginationGoToFirstPage();
			}
		},
		handleResetFilters() {
			this.dateFilter = 'past3Months';
			this.customDateRange = null;

			this.appliedFilters = {
				dateFilter: 'past3Months',
				customDateRange: null
			};

			if (this.gridApi) {
				this.gridApi.setFilterModel(null);
				this.gridApi.paginationGoToFirstPage();
			}

			this.fetchExecutions();
		},
		handleDateFilterChange() {
			if (this.dateFilter !== 'custom') {
				this.customDateRange = null;
			}
		},
		onGridReady(params: any) {
			this.gridApi = params.api;
			this.gridApi.autoSizeAllColumns();
		},
		handleDownload(report: any) {
			if (!report.report_path) {
				this.$message.warning(this.$t('reports.report_expired'));
				return;
			}

			const attachment: Uri_Attachment = {
				scheme: 'uri',
				name: report.name,
				content_type: null,
				uri: report.report_path
			};

			attachmentDownload(this.selectedParticipantStore, attachment);
		},
		formatDate(dateString: string | null | undefined): string {
			if (!dateString) return '-';
			const date = new Date(dateString);
			if (isNaN(date.getTime())) return dateString;
			return parseTime(date, '{d}-{m}-{y}') || dateString;
		}
	}
});
